import { useSearchParams } from "react-router-dom";
import { toPriority, type WorkOrderKind, type WorkOrderStatus } from "../../domain/workOrders";
import {
  DATE_FIELDS,
  SLA_STATES,
  SORTS,
  type DateField,
  type SlaState,
  type Scope,
  type SortKey,
  type WorkOrderFilters,
} from "../../domain/workOrderReport";

/**
 * The work orders filters, held in the URL.
 *
 * In the URL rather than in state so the list, its report and a link sent to a
 * colleague all describe the same set: "Generate report" carries the query
 * string across, and the report reads it back through this same hook.
 */
export function useWorkOrderFilters(): {
  filters: WorkOrderFilters;
  params: URLSearchParams;
  set: (key: string, value: string | null) => void;
  setMany: (values: Record<string, string | null>) => void;
  clear: () => void;
} {
  const [params, setParams] = useSearchParams();

  const setMany = (values: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const dateField = params.get("date") as DateField | null;
  const sort = params.get("sort") as SortKey | null;
  const filters: WorkOrderFilters = {
    scope:
      (params.get("scope") as Scope) ||
      (params.get("status") || params.get("from") || params.get("to") ? "all" : "open"),
    status: (params.get("status") as WorkOrderStatus | null) || null,
    priority: params.get("priority") ? toPriority(Number(params.get("priority"))) : null,
    kind: (params.get("kind") as WorkOrderKind | null) || null,
    repairType: params.get("repair") || null,
    discipline: params.get("discipline") || null,
    area: params.get("area") || null,
    trade: params.get("trade") || null,
    contractor: params.get("contractor") || null,
    sla: SLA_STATES.includes(params.get("sla") as SlaState) ? (params.get("sla") as SlaState) : null,
    overdueOnly: params.get("overdue") === "1",
    q: params.get("q") ?? "",
    dateField: dateField && DATE_FIELDS.includes(dateField) ? dateField : "opened",
    from: params.get("from") || undefined,
    to: params.get("to") || undefined,
    sort: sort && SORTS.includes(sort) ? sort : "priority",
  };

  return {
    filters,
    params,
    set: (key, value) => setMany({ [key]: value }),
    setMany,
    clear: () => setParams(new URLSearchParams(), { replace: true }),
  };
}
